import { Link } from "react-router";
import { Clock } from "lucide-react";
import { cn } from "@veo/ui";
import { fetchContent } from "../lib/api";
import { plainText, truncate } from "../lib/richtext";
import { absoluteUrl } from "../lib/site";
import { ArticleCard, type PostSummary } from "../components/ArticleCard";
import { formatDate } from "../lib/format";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ResponsiveImage } from "../components/ResponsiveImage";
import { RichText } from "../components/RichText";
import { Reveal, RevealItem, RevealList } from "../components/motion";
import type { Route } from "./+types/article";

interface Article extends PostSummary {
  content: { type: string; content?: unknown[] } | null;
  contentHtml: string | null;
  tags: { slug: string; name: string }[];
  author: {
    name: string;
    slug: string;
    role: string | null;
    /**
     * A list, as it is on the team member it comes from — not a string.
     *
     * This was typed `string | null`, which is why the byline read
     * "CPA-KBCom FinanceMBA Finance": nothing separated the entries, because
     * nothing knew there were entries. React renders an array of strings by
     * placing them adjacent, and a template literal falls back to
     * `Array.toString()` and joins on a bare comma. Both are what you get for
     * free when the type says string and the value is not one.
     */
    credentials: string[];
    photoUrl: string | null;
  } | null;
  seo: {
    metaTitle: string | null;
    metaDescription: string | null;
    ogTitle: string | null;
    ogDescription: string | null;
    ogImageUrl: string | null;
    canonicalUrl: string | null;
    noIndex: boolean;
  } | null;
  related: PostSummary[];
}

export async function loader({ params }: Route.LoaderArgs) {
  return { article: await fetchContent<Article>(`/public/posts/${params.slug}`) };
}

export function meta({ loaderData, params }: Route.MetaArgs) {
  if (!loaderData?.article) return [{ title: "Article — VEO Consultancy" }];

  const { article } = loaderData;
  const title = article.seo?.metaTitle ?? `${article.title} — VEO Consultancy`;
  const description =
    article.seo?.metaDescription ?? article.excerpt ?? truncate(plainText(article.content), 155);
  const image = article.seo?.ogImageUrl ?? article.coverImage?.url;
  const canonical = article.seo?.canonicalUrl ?? absoluteUrl(`/insights/${params.slug}`);

  return [
    { title },
    { name: "description", content: description },
    ...(article.seo?.noIndex ? [{ name: "robots", content: "noindex, nofollow" }] : []),
    { property: "og:type", content: "article" },
    { property: "og:title", content: article.seo?.ogTitle ?? title },
    { property: "og:description", content: article.seo?.ogDescription ?? description },
    { property: "og:url", content: canonical },
    ...(image ? [{ property: "og:image", content: image }] : []),
    ...(article.publishedAt
      ? [{ property: "article:published_time", content: article.publishedAt }]
      : []),
    ...(article.author ? [{ property: "article:author", content: article.author.name }] : []),
    { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
    { name: "twitter:title", content: article.seo?.ogTitle ?? title },
    { name: "twitter:description", content: article.seo?.ogDescription ?? description },
    ...(image ? [{ name: "twitter:image", content: image }] : []),
    { tagName: "link", rel: "canonical", href: canonical },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description,
        ...(image ? { image: [image] } : {}),
        ...(article.publishedAt ? { datePublished: article.publishedAt } : {}),
        ...(article.author ? { author: { "@type": "Person", name: article.author.name } } : {}),
        publisher: {
          "@type": "Organization",
          name: "VEO Consultancy",
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      },
    },
  ];
}

export default function ArticlePage({ loaderData }: Route.ComponentProps) {
  const { article } = loaderData;

  return (
    <main id="main">
      <article>
        <header className="bg-navy-900 textured-dark text-cream-100">
          <div className="container-site section-y">
            <Breadcrumbs
              onDark
              className="mb-6"
              items={[
                { label: "Insights", href: "/insights" },
                ...(article.category
                  ? [
                      {
                        label: article.category.name,
                        href: `/insights/category/${article.category.slug}`,
                      },
                    ]
                  : []),
                { label: article.title },
              ]}
            />

            <div className="container-content">
              {article.category && (
                <Link
                  to={`/insights/category/${article.category.slug}`}
                  className="eyebrow text-gold-300 hover:text-gold-200"
                >
                  {article.category.name}
                </Link>
              )}

              <h1 className="mt-4 text-5xl text-cream-50">{article.title}</h1>

              {article.excerpt && (
                <p className="mt-6 text-lg leading-relaxed text-navy-100">{article.excerpt}</p>
              )}

              {/*
                Who wrote it, then when — two rows rather than one.

                Credentials are a list of unpredictable length, so any layout
                that seats them on the same line as the date is one qualified
                author away from wrapping badly. Given their own row they can
                be as long as they are, and the date and reading time keep the
                tidy single line they had before.
              */}
              <div className="mt-8 text-sm text-navy-300">
                {article.author && (
                  <div className="flex items-center gap-3">
                    {article.author.photoUrl && (
                      <img
                        src={article.author.photoUrl}
                        alt=""
                        width={36}
                        height={36}
                        loading="lazy"
                        className="size-9 shrink-0 rounded-full object-cover"
                      />
                    )}
                    {/* Name over credentials, not beside them: five
                        qualifications outrun the name several times over, and
                        on one line they wrapped it in half. */}
                    <span className="flex flex-col gap-1">
                      <span className="text-cream-100">{article.author.name}</span>
                      {article.author.credentials.length > 0 && (
                        <span className="text-xs leading-relaxed text-navy-300">
                          {article.author.credentials.join(" · ")}
                        </span>
                      )}
                    </span>
                  </div>
                )}

                <div
                  className={cn(
                    "flex flex-wrap items-center gap-x-5 gap-y-2",
                    article.author && "mt-5",
                  )}
                >
                  {article.publishedAt && (
                    <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>
                  )}

                  {article.readingMinutes && (
                    <span className="flex items-center gap-1.5">
                      <Clock aria-hidden className="size-4" />
                      {article.readingMinutes} min read
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {article.coverImage && (
          <div className="bg-navy-900">
            <div className="container-site">
              <ResponsiveImage
                image={article.coverImage}
                priority
                sizes="(min-width: 1280px) 1120px, 100vw"
                className="w-full rounded-xl object-cover"
              />
            </div>
          </div>
        )}

        <div className="bg-surface-0 textured">
          <div className="container-site section-y">
            <div className="container-content">
              <RichText doc={article.content} className="text-lg" />

              {article.tags.length > 0 && (
                <div className="mt-12 border-t border-ink-200 pt-6">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                    Topics
                  </h2>
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <li
                        key={tag.slug}
                        className="rounded-full bg-cream-200 px-3.5 py-1.5 text-xs font-medium text-navy-800"
                      >
                        {tag.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Either half is worth the card on its own — an author with
                  qualifications but no job title still has something to say
                  here, and the `role`-only guard used to drop them both. */}
              {article.author && (article.author.role || article.author.credentials.length > 0) && (
                <div className="mt-10 rounded-xl bg-cream-200 p-7">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gold-700">
                    Written by
                  </p>
                  <p className="mt-2 font-display text-xl text-navy-900">{article.author.name}</p>
                  {article.author.role && (
                    <p className="mt-1 text-sm text-ink-600">{article.author.role}</p>
                  )}

                  {/*
                    A list, laid out as one — the same treatment and the same
                    reasoning as the team profile row.

                    The role already contains a comma ("Lead Advisor, VEO
                    Consultancy"), so running five qualifications onto the end
                    of it behind a `·` gives one long line with three different
                    separators doing three different jobs. This is the block on
                    the page with room to spare; spending it on a line each is
                    what makes the qualifications scannable rather than merely
                    present.
                  */}
                  {article.author.credentials.length > 0 && (
                    <ul className="mt-5 space-y-2 border-t border-cream-300 pt-5 text-xs uppercase tracking-wider text-ink-500">
                      {article.author.credentials.map((credential) => (
                        <li key={credential} className="flex gap-2.5">
                          <span
                            aria-hidden
                            className="mt-1.5 size-1 shrink-0 rounded-full bg-gold-500"
                          />
                          {credential}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/*
                An article about investing that ends without a reminder of what
                it is not is the kind of omission a regulator notices. The
                wording itself is a CMS setting; only its presence is fixed.
              */}
              <p className="mt-10 border-l-2 border-gold-500 pl-5 text-sm leading-relaxed text-ink-500">
                This article is general information, not personalised investment advice. Please
                speak to an adviser about your own circumstances before acting on it.
              </p>
            </div>
          </div>
        </div>

        <section className="bg-surface-100 textured">
          <div className="container-site section-y">
            <Reveal className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="eyebrow">Keep reading</p>
                <h2 className="rule-accent mt-3 text-4xl">Related insights</h2>
              </div>
              <Link
                to="/insights"
                className="text-sm font-semibold text-navy-700 underline decoration-gold-500 underline-offset-4"
              >
                All insights
              </Link>
            </Reveal>

            {article.related.length > 0 ? (
              <RevealList className="mt-12 grid gap-10 md:grid-cols-3">
                {article.related.map((post) => (
                  <RevealItem key={post.id}>
                    <ArticleCard post={post} showExcerpt={false} />
                  </RevealItem>
                ))}
              </RevealList>
            ) : (
              <p className="mt-8 text-ink-600">
                This is our latest piece in this category — more is on the way.
              </p>
            )}
          </div>
        </section>
      </article>
    </main>
  );
}
